import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../firebase-config";
import { doc, getDoc, setDoc, collection, getDocs, addDoc, deleteDoc } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { workSessionService } from '../services/workSessionService';
import "./ConfigurationPage.css";
import { parseISODate, getMonday, getSunday, formatDateFR, formatDateYYYYMMDD } from '../utils/dateUtils';

const ADMIN_COLUMNS = [
  { key: "nom", label: "Nom" },
  { key: "date", label: "Date" },
  { key: "heureDebut", label: "Heure Début" },
  { key: "heureFin", label: "Heure Fin" },
  { key: "travail", label: "Temps Travail" },
  { key: "pause", label: "Temps Pause" },
  { key: "site", label: "Site" },
  { key: "gpsDebut", label: "GPS Début" },
  { key: "gpsFin", label: "GPS Fin" },
  { key: "zoneDebut", label: "Zone Début" },
  { key: "zoneFin", label: "Zone Fin" },
  { key: "zonePause", label: "Zone(s) Pause(s)" },
  { key: "appareil", label: "Appareil" }
];

const MAP_STYLES = [
  {
    key: "osm",
    label: "OpenStreetMap Standard",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '&copy; OpenStreetMap contributors'
  },
  {
    key: "cartodb_dark",
    label: "CartoDB Dark Matter (sombre)",
    url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
    attribution: '&copy; <a href="https://carto.com/attributions">CARTO</a> &copy; OpenStreetMap contributors'
  },
  {
    key: "cartodb",
    label: "CartoDB Positron (clair)",
    url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
    attribution: '&copy; <a href="https://carto.com/attributions">CARTO</a> &copy; OpenStreetMap contributors'
  },
  {
    key: "stadia_toner",
    label: "Stadia Stamen Toner (noir & blanc)",
    url: "https://tiles.stadiamaps.com/tiles/stamen_toner/{z}/{x}/{y}{r}.png?api_key=8130683a-487e-49be-bbce-7bf5f7a43889",
    attribution: '&copy; <a href="https://www.stadiamaps.com/" target="_blank">Stadia Maps</a> &copy; <a href="https://www.stamen.com/" target="_blank">Stamen Design</a> &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
  }
];

function ConfigurationPage() {
  const navigate = useNavigate();
  const [selectedStyle, setSelectedStyle] = useState('osm');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [creatingTimesheets, setCreatingTimesheets] = useState(false);
  const [timesheetMessage, setTimesheetMessage] = useState("");
  const [selectedWeek, setSelectedWeek] = useState("");
  const [forceUpdate, setForceUpdate] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // États pour l'archivage
  const [archiving, setArchiving] = useState(false);
  const [archiveMessage, setArchiveMessage] = useState("");
  const [archiveStats, setArchiveStats] = useState(null);

  // PDF/CSV config states
  const [pdfColumns, setPdfColumns] = useState(() => ADMIN_COLUMNS.map(col => ({ ...col, checked: true, width: Math.round(100 / ADMIN_COLUMNS.length) })));
  const [csvColumns, setCsvColumns] = useState(() => ADMIN_COLUMNS.map(col => ({ ...col, checked: true })));
  const [pdfOrientation, setPdfOrientation] = useState('landscape');
  const [pdfShowHoraeLogo, setPdfShowHoraeLogo] = useState(() => localStorage.getItem('pdfShowHoraeLogo') === 'true');

  // Temps de session max (alerte)
  const [maxSessionTime, setMaxSessionTime] = useState(12); // 12h par défaut
  useEffect(() => {
    const ref = doc(db, "adminConfigs", "global");
    getDoc(ref).then(snap => {
      if (snap.exists() && snap.data().maxSessionTimeHours) {
        setMaxSessionTime(Number(snap.data().maxSessionTimeHours));
      }
    });
  }, []);

  const saveMaxSessionTime = async () => {
    const ref = doc(db, "adminConfigs", "global");
    await setDoc(ref, { maxSessionTimeHours: Number(maxSessionTime) }, { merge: true });
  };

  // Charger la config Firestore au montage
  useEffect(() => {
    const fetchConfig = async () => {
      console.log("Chargement de la configuration...");
      const user = auth.currentUser;
      if (!user) {
        console.log("Aucun utilisateur connecté");
        return;
      }
      const ref = doc(db, "adminConfigs", "global");
      const snap = await getDoc(ref);
      console.log("Configuration trouvée:", snap.exists() ? snap.data() : "non trouvée");
      
      if (snap.exists()) {
        const data = snap.data();
        if (data.mapStyle) {
          console.log("Style de carte trouvé:", data.mapStyle);
          setSelectedStyle(data.mapStyle);
        }
        if (data.pdfColumns) setPdfColumns(data.pdfColumns);
        if (data.csvColumns) setCsvColumns(data.csvColumns);
        if (data.pdfOrientation) setPdfOrientation(data.pdfOrientation);
        if (data.pdfShowHoraeLogo !== undefined) setPdfShowHoraeLogo(data.pdfShowHoraeLogo);
      }
    };
    fetchConfig();
  }, []);

  // Sauvegarder la config Firestore
  const saveConfig = async (type, mapStyleValue) => {
    console.log("Sauvegarde de la configuration:", type);
    setSaving(true);
    setSaveMsg("");
    const user = auth.currentUser;
    if (!user) {
      console.log("Erreur: utilisateur non connecté");
      setSaveMsg("Erreur : utilisateur non connecté");
      setSaving(false);
      return;
    }
    const ref = doc(db, "adminConfigs", "global");
    let update = {};
    if (type === "pdf") {
      update = { pdfColumns, pdfOrientation, pdfShowHoraeLogo };
    } else if (type === "csv") {
      update = { csvColumns };
    } else if (type === "map") {
      console.log("Sauvegarde du style de carte:", mapStyleValue);
      update = { mapStyle: mapStyleValue };
    }
    try {
      await setDoc(ref, update, { merge: true });
      console.log("Configuration sauvegardée avec succès");
      setSaveMsg("Configuration enregistrée !");
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
      setSaveMsg("Erreur lors de la sauvegarde");
    }
    setSaving(false);
    setTimeout(() => setSaveMsg(""), 2000);
  };

  // Gestion sliders (PDF) : répartition stricte et fluide
  const handlePdfWidthChange = (idx, value) => {
    let cols = [...pdfColumns];
    const minWidth = 1;
    const checkedIdxs = cols.map((col, i) => col.checked ? i : null).filter(i => i !== null);
    if (checkedIdxs.length === 1) {
      cols[idx].width = 100;
    } else {
      // Fixe la colonne modifiée à la valeur demandée
      cols[idx].width = value;
      // Reste à répartir
      let rest = 100 - value;
      // Indices des autres colonnes cochées
      const others = checkedIdxs.filter(i => i !== idx);
      // Somme des largeurs des autres
      let sumOthers = others.reduce((sum, i) => sum + cols[i].width, 0);
      // Répartition stricte
      let newWidths = others.map(i => Math.max(minWidth, (cols[i].width / sumOthers) * rest));
      // Correction si une colonne tombe sous minWidth
      let totalMin = newWidths.filter(w => w === minWidth).length * minWidth;
      let restForOthers = rest - totalMin;
      let sumNonMin = others.reduce((sum, i, j) => sum + (newWidths[j] > minWidth ? cols[i].width : 0), 0);
      // Redistribuer le reste sur les colonnes non bloquées
      newWidths = newWidths.map((w, j) => w === minWidth ? minWidth : (cols[others[j]].width / sumNonMin) * restForOthers);
      // Arrondi et correction finale
      let intWidths = newWidths.map(w => Math.round(w));
      let sum = intWidths.reduce((a, b) => a + b, 0) + Math.round(value);
      let delta = 100 - sum;
      if (others.length > 0) {
        intWidths[intWidths.length - 1] += delta;
      }
      others.forEach((i, j) => {
        cols[i].width = Math.max(intWidths[j], minWidth);
      });
    }
    setPdfColumns(cols);
  };

  // Quand on décoche une colonne, répartir son pourcentage sur les autres cochées
  const handlePdfCheck = idx => {
    setPdfColumns(cols => {
      const checkedCols = cols.filter((col, i) => col.checked && i !== idx);
      const uncheckedCol = cols[idx];
      if (!cols[idx].checked && checkedCols.length > 0) {
        // On coche : donner une largeur moyenne
        const avg = Math.round(100 / (checkedCols.length + 1));
        return cols.map((col, i) =>
          i === idx ? { ...col, checked: true, width: avg } : col.checked ? { ...col, width: avg } : col
        );
      } else if (cols[idx].checked && checkedCols.length > 0) {
        // On décoche : répartir la largeur de la colonne décochée
        const toDistribute = uncheckedCol.width;
        const sumOthers = checkedCols.reduce((sum, col) => sum + col.width, 0);
        return cols.map((col, i) => {
          if (i === idx) return { ...col, checked: false };
          if (col.checked) {
            let newWidth = col.width + Math.round((col.width / sumOthers) * toDistribute);
            return { ...col, width: newWidth };
          }
          return col;
        });
      } else {
        // Cas limite : on décoche la dernière colonne cochée
        return cols.map((col, i) => i === idx ? { ...col, checked: false, width: 0 } : col);
      }
    });
  };

  // Gestion cases à cocher
  const handleCsvCheck = idx => setCsvColumns(cols => cols.map((col, i) => i === idx ? { ...col, checked: !col.checked } : col));

  // Restaurer valeurs par défaut
  const restorePdfDefaults = () => setPdfColumns(ADMIN_COLUMNS.map(col => ({ ...col, checked: true, width: Math.round(100 / ADMIN_COLUMNS.length) })));
  const restoreCsvDefaults = () => setCsvColumns(ADMIN_COLUMNS.map(col => ({ ...col, checked: true })));

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/");
  };

  // Générer les options du sélecteur (4 dernières semaines)
  const generateWeekOptions = () => {
    const options = [];
    const now = new Date();
    console.log('[DEBUG] Date actuelle (now) :', now.toLocaleString('fr-FR'), 'Jour:', now.getDay());
    // S'assurer que nous partons d'un lundi, même si on est dimanche
    const currentMonday = getMonday(now);
    console.log('[DEBUG] Génération options - Lundi de référence:', currentMonday.toLocaleString('fr-FR'));

    for (let i = 0; i < 4; i++) {
      const weekMonday = new Date(currentMonday);
      weekMonday.setDate(currentMonday.getDate() - 7 * i);
      
      // Vérification explicite que c'est bien un lundi
      if (weekMonday.getDay() !== 1) {
        console.error('[ERREUR] Le sélecteur ne propose pas un lundi !', weekMonday.toLocaleString('fr-FR'));
        continue; // Skip cette semaine si ce n'est pas un lundi
      }

      const weekSunday = getSunday(weekMonday);
      // Utiliser formatDateYYYYMMDD pour éviter le bug UTC
      const weekStart = formatDateYYYYMMDD(weekMonday);
      const label = `Semaine du ${formatDateFR(weekMonday)} au ${formatDateFR(weekSunday)}`;
      
      console.log('[DEBUG] Option générée:', {
        value: weekStart,
        label,
        lundi: weekMonday.toLocaleString('fr-FR'),
        dimanche: weekSunday.toLocaleString('fr-FR')
      });
      
      options.push({ value: weekStart, label });
    }
    return options;
  };

  // Fonction pour créer manuellement les feuilles de temps
  const createWeeklyTimesheets = async () => {
    if (!selectedWeek) {
      setTimesheetMessage("Erreur: Veuillez sélectionner une semaine");
      return;
    }
    setCreatingTimesheets(true);
    setTimesheetMessage("");
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("Utilisateur non connecté");
      }
      // Récupérer tous les employés
      const employeesSnap = await getDocs(collection(db, 'employees'));
      const employees = employeesSnap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(emp => emp.role === 'employee');
      // Utiliser parseISODate(selectedWeek) comme lundi
      const weekStartDate = parseISODate(selectedWeek);
      weekStartDate.setHours(0, 0, 0, 0);
      let created = 0;
      let updated = 0;
      let skipped = 0;
      let errors = 0;
      for (const emp of employees) {
        try {
          const weekId = `${emp.email}_${weekStartDate.toISOString().slice(0, 10)}`;
          const weekRef = doc(db, 'workSessionsWeek', weekId);
          const weekDoc = await getDoc(weekRef);
          if (weekDoc.exists()) {
            if (forceUpdate) {
              await workSessionService.createOrUpdateWorkSessionsWeek(emp, weekStartDate);
              updated++;
            } else {
              skipped++;
              continue;
            }
          } else {
            await workSessionService.createOrUpdateWorkSessionsWeek(emp, weekStartDate);
            created++;
          }
        } catch (error) {
          errors++;
          console.error(`[ERREUR] Création feuille de temps pour ${emp.email} (${emp.nom || ''}):`, error);
        }
      }
      let message = [];
      if (created > 0) message.push(`${created} feuille(s) créée(s)`);
      if (updated > 0) message.push(`${updated} feuille(s) mise(s) à jour`);
      if (skipped > 0) message.push(`${skipped} feuille(s) ignorée(s) (déjà existante(s))`);
      if (errors > 0) message.push(`${errors} erreur(s)`);
      setTimesheetMessage(message.join(", "));
    } catch (error) {
      setTimesheetMessage(`Erreur: ${error.message}`);
    } finally {
      setCreatingTimesheets(false);
    }
  };

  // Fonction pour prévisualiser les feuilles de temps existantes
  const previewWeeklyTimesheets = async () => {
    if (!selectedWeek) {
      setTimesheetMessage("Erreur: Veuillez sélectionner une semaine");
      return;
    }

    setLoadingPreview(true);
    setPreviewData(null);
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("Utilisateur non connecté");
      }
      // Récupérer tous les employés
      const employeesSnap = await getDocs(collection(db, 'employees'));
      const employees = employeesSnap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(emp => emp.role === 'employee');
      
      // Utiliser directement la date sélectionnée
      const weekStartDate = parseISODate(selectedWeek);
      const weekEndDate = getSunday(weekStartDate);
      
      // LOGS EXPLICITES EN LOCAL
      console.log('[DEBUG] Prévisualisation - Semaine du', weekStartDate.toLocaleString('fr-FR'), 'au', weekEndDate.toLocaleString('fr-FR'));
      
      const preview = {
        weekStart: selectedWeek,
        weekEnd: weekEndDate.toISOString().slice(0, 10),
        employees: []
      };
      
      for (const emp of employees) {
        const weekId = `${emp.email}_${selectedWeek}`;
        const weekRef = doc(db, 'workSessionsWeek', weekId);
        const weekDoc = await getDoc(weekRef);
        const weekData = weekDoc.exists() ? weekDoc.data() : null;
        const employeeData = {
          email: emp.email,
          name: emp.name || `${emp.prenom || ''} ${emp.nom || ''}`.trim(),
          exists: weekDoc.exists(),
          status: weekData?.status || "Non créée",
          signed: weekData?.signed || false,
          sessionsCount: weekData?.sessions?.length || 0,
          absencesCount: weekData?.absences?.length || 0,
          lastUpdate: weekData?.updatedAt ? new Date(weekData.updatedAt.seconds * 1000) : null
        };
        preview.employees.push(employeeData);
      }
      setPreviewData(preview);
    } catch (error) {
      setTimesheetMessage(`Erreur: ${error.message}`);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Fonction pour formater la date de dernière mise à jour
  const formatLastUpdate = (date) => {
    if (!date) return "Jamais";
    const d = new Date(date);
    return d.toLocaleString('fr-FR');
  };

  // Fonction d'archivage des feuilles de temps
  const handleArchiveTimesheets = async () => {
    setArchiving(true);
    setArchiveMessage("");
    setArchiveStats(null);

    try {
      console.log("🔄 Début de l'archivage des feuilles de temps...");
      
      // Récupérer toutes les feuilles de temps
      const timesheetsRef = collection(db, "workSessionsWeek");
      const timesheetsSnapshot = await getDocs(timesheetsRef);
      
      if (timesheetsSnapshot.empty) {
        setArchiveMessage("Aucune feuille de temps à archiver.");
        setArchiving(false);
        return;
      }

      // Convertir en array
      const allTimesheets = timesheetsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log(`📊 Total feuilles de temps: ${allTimesheets.length}`);

      // Grouper par employeeEmail
      const timesheetsByEmployee = {};
      allTimesheets.forEach(timesheet => {
        const email = timesheet.employeeEmail;
        if (!timesheetsByEmployee[email]) {
          timesheetsByEmployee[email] = [];
        }
        timesheetsByEmployee[email].push(timesheet);
      });

      console.log(`👥 Nombre d'employés: ${Object.keys(timesheetsByEmployee).length}`);

      // Pour chaque employé, trier par date et garder les 12 plus récentes
      const toKeep = [];
      const toArchive = [];

      Object.entries(timesheetsByEmployee).forEach(([email, timesheets]) => {
        // Trier par weekStart (plus récentes en premier)
        timesheets.sort((a, b) => {
          const dateA = new Date(a.weekStart);
          const dateB = new Date(b.weekStart);
          return dateB - dateA;
        });

        console.log(`👤 ${email}: ${timesheets.length} feuilles`);

        // Garder les 12 plus récentes pour cet employé
        const employeeToKeep = timesheets.slice(0, 12);
        const employeeToArchive = timesheets.slice(12);

        toKeep.push(...employeeToKeep);
        toArchive.push(...employeeToArchive);

        console.log(`   → Garder: ${employeeToKeep.length}, Archiver: ${employeeToArchive.length}`);
      });

      console.log(`📁 Total à garder: ${toKeep.length}, Total à archiver: ${toArchive.length}`);

      if (toArchive.length === 0) {
        setArchiveMessage("Aucune feuille de temps à archiver (tous les employés ont moins de 12 mois).");
        setArchiving(false);
        return;
      }

      // Archiver les anciennes feuilles
      const currentUser = auth.currentUser;
      const archivePromises = toArchive.map(async (timesheet) => {
        // Ajouter à la collection d'archives
        await addDoc(collection(db, "workSessionsWeekArchives"), {
          ...timesheet,
          archivedAt: new Date(),
          archivedBy: currentUser?.email || "admin",
          originalId: timesheet.id
        });

        // Supprimer de la collection principale
        await deleteDoc(doc(db, "workSessionsWeek", timesheet.id));
      });

      await Promise.all(archivePromises);

      // Calculer les statistiques détaillées
      const employeeStats = Object.entries(timesheetsByEmployee).map(([email, timesheets]) => {
        const sorted = timesheets.sort((a, b) => new Date(b.weekStart) - new Date(a.weekStart));
        const kept = sorted.slice(0, 12);
        const archived = sorted.slice(12);
        return {
          email,
          total: timesheets.length,
          kept: kept.length,
          archived: archived.length
        };
      });

      const stats = {
        totalBefore: allTimesheets.length,
        kept: toKeep.length,
        archived: toArchive.length,
        totalAfter: toKeep.length,
        employees: employeeStats
      };

      setArchiveStats(stats);
      setArchiveMessage(`✅ Archivage terminé ! ${toArchive.length} feuilles archivées, ${toKeep.length} conservées.`);
      
      console.log("✅ Archivage terminé avec succès");

    } catch (error) {
      console.error("❌ Erreur lors de l'archivage:", error);
      setArchiveMessage(`❌ Erreur lors de l'archivage: ${error.message}`);
    }

    setArchiving(false);
  };

  // Archivage manuel des sessions de plus de 100 jours
  const [archivingSessions, setArchivingSessions] = useState(false);
  const [archiveSessionsMsg, setArchiveSessionsMsg] = useState("");

  const handleArchiveSessions = async () => {
    setArchivingSessions(true);
    setArchiveSessionsMsg("");
    try {
      const now = new Date();
      const cutoff = new Date(now.getTime() - 100 * 24 * 60 * 60 * 1000); // 100 jours
      const sessionsSnap = await getDocs(collection(db, "workSessions"));
      let moved = 0;
      for (const docSnap of sessionsSnap.docs) {
        const session = docSnap.data();
        let sessionDate = session.startTime?.toDate ? session.startTime.toDate() : (session.startTime?.seconds ? new Date(session.startTime.seconds * 1000) : null);
        if (!sessionDate) continue;
        if (sessionDate < cutoff) {
          // Déplacer la session
          await setDoc(doc(db, "workSessionsArchive", docSnap.id), session);
          await deleteDoc(doc(db, "workSessions", docSnap.id));
          // Déplacer les pauses associées
          const breaksSnap = await getDocs(collection(db, "breaks"));
          for (const breakDoc of breaksSnap.docs) {
            const breakData = breakDoc.data();
            if (breakData.sessionId === docSnap.id) {
              await setDoc(doc(db, "breaksArchive", breakDoc.id), breakData);
              await deleteDoc(doc(db, "breaks", breakDoc.id));
            }
          }
          moved++;
        }
      }
      setArchiveSessionsMsg(`Archivage terminé : ${moved} session(s) déplacée(s) dans les archives.`);
    } catch (e) {
      setArchiveSessionsMsg("Erreur lors de l'archivage des sessions : " + e.message);
    }
    setArchivingSessions(false);
  };

  return (
    <div style={{ maxWidth: 900, margin: '40px auto', padding: 24, background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', position: 'relative' }}>
      <button className="admin-button" onClick={() => navigate("/admin")} style={{ marginBottom: 16 }}>Retour Admin</button>
      <h1>Configuration</h1>
      <div style={{ margin: '32px 0' }}>
        <label style={{ fontWeight: 'bold', fontSize: 18 }}>Style de la carte :</label>
        <div style={{ marginTop: 12 }}>
          {MAP_STYLES.map(style => (
            <label key={style.key} style={{ display: 'block', marginBottom: 10 }}>
              <input
                type="radio"
                name="mapStyle"
                value={style.key}
                checked={selectedStyle === style.key}
                onChange={() => {
                  setSelectedStyle(style.key);
                  saveConfig("map", style.key);
                }}
                style={{ marginRight: 8 }}
              />
              {style.label}
            </label>
          ))}
        </div>
        {saveMsg && <p style={{ color: '#4CAF50', marginTop: 10 }}>{saveMsg}</p>}
      </div>

      {/* PDF CONFIG */}
      <div style={{ margin: '32px 0', border: '1px solid #eee', borderRadius: 8, padding: 20 }}>
        <h2>Configuration export PDF</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
          <div style={{ flex: 2 }}>
            {pdfColumns.map((col, idx) => (
              <div key={col.key} style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                <input type="checkbox" checked={col.checked} onChange={() => handlePdfCheck(idx)} />
                <span style={{ marginLeft: 8, minWidth: 120 }}>{col.label}</span>
                <input
                  type="range"
                  min={5}
                  max={50}
                  value={col.width}
                  onChange={e => handlePdfWidthChange(idx, Number(e.target.value))}
                  style={{ marginLeft: 16, flex: 1 }}
                  disabled={!col.checked}
                />
                <span style={{ width: 40, textAlign: 'right', marginLeft: 8 }}>{col.width}%</span>
              </div>
            ))}
            <div style={{ marginTop: 16 }}>
              <label>Format PDF : </label>
              <select value={pdfOrientation} onChange={e => setPdfOrientation(e.target.value)} style={{ marginLeft: 8 }}>
                <option value="portrait">Portrait</option>
                <option value="landscape">Paysage</option>
              </select>
            </div>
            <div style={{ marginTop: 16 }}>
              <label>
                <input type="checkbox" checked={pdfShowHoraeLogo} onChange={e => setPdfShowHoraeLogo(e.target.checked)} />
                Afficher le logo HORAE (coin haut droit)
              </label>
            </div>
            <div className="button-row">
              <button className="save-button" onClick={() => saveConfig("pdf")} disabled={saving}>Enregistrer</button>
              <button className="reset-button" onClick={restorePdfDefaults} disabled={saving}>Restaurer PDF par défaut</button>
            </div>
            {saveMsg && <div style={{ color: 'green', marginTop: 8 }}>{saveMsg}</div>}
          </div>
        </div>
      </div>

      {/* CSV CONFIG */}
      <div style={{ margin: '32px 0', border: '1px solid #eee', borderRadius: 8, padding: 20 }}>
        <h2>Configuration export CSV</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
          <div style={{ flex: 2 }}>
            {csvColumns.map((col, idx) => (
              <div key={col.key} style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                <input type="checkbox" checked={col.checked} onChange={() => handleCsvCheck(idx)} />
                <span style={{ marginLeft: 8 }}>{col.label}</span>
              </div>
            ))}
            <div className="button-row">
              <button className="save-button" onClick={() => saveConfig("csv")} disabled={saving}>Enregistrer</button>
              <button className="reset-button" onClick={restoreCsvDefaults} disabled={saving}>Restaurer CSV par défaut</button>
            </div>
            {saveMsg && <div style={{ color: 'green', marginTop: 8 }}>{saveMsg}</div>}
          </div>
        </div>
      </div>

      <div className="config-section">
        <h2>Gestion des feuilles de temps</h2>
        <div className="config-content">
          <div className="timesheet-controls">
            <select 
              value={selectedWeek} 
              onChange={(e) => {
                setSelectedWeek(e.target.value);
                console.log('[DEBUG] Semaine sélectionnée (brute):', e.target.value);
              }}
              className="week-select"
            >
              <option value="">Sélectionner une semaine</option>
              {generateWeekOptions().map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <div className="timesheet-buttons">
              <button 
                className="admin-button preview-button" 
                onClick={previewWeeklyTimesheets}
                disabled={loadingPreview || !selectedWeek}
              >
                {loadingPreview ? "Chargement..." : "Prévisualiser"}
              </button>

              <div className="force-update-checkbox">
                <input
                  type="checkbox"
                  id="forceUpdate"
                  checked={forceUpdate}
                  onChange={(e) => setForceUpdate(e.target.checked)}
                />
                <label htmlFor="forceUpdate">
                  Forcer la mise à jour des feuilles existantes
                </label>
              </div>

              <button 
                className="admin-button" 
                onClick={createWeeklyTimesheets}
                disabled={creatingTimesheets || !selectedWeek}
              >
                {creatingTimesheets ? "Création en cours..." : "Créer les feuilles de temps"}
              </button>
            </div>
          </div>

          {previewData && (() => {
            const weekStartDate = parseISODate(selectedWeek);
            const weekEndDate = getSunday(weekStartDate);
            // LOGS EXPLICITES EN LOCAL
            console.log('[DEBUG] Affichage prévisualisation - Semaine du', weekStartDate.toLocaleString('fr-FR'), 'au', weekEndDate.toLocaleString('fr-FR'));
            return (
              <div className="preview-section">
                <h3>Prévisualisation pour la semaine du {formatDateFR(weekStartDate)} au {formatDateFR(weekEndDate)}</h3>
                <div className="preview-table-container">
                  <table className="preview-table">
                    <thead>
                      <tr>
                        <th>Employé</th>
                        <th>Statut</th>
                        <th>Signée</th>
                        <th>Sessions</th>
                        <th>Absences</th>
                        <th>Dernière mise à jour</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.employees.map((emp, index) => (
                        <tr key={index} className={emp.exists ? "exists" : "new"}>
                          <td>{emp.name}</td>
                          <td>{emp.status}</td>
                          <td>{emp.signed ? "✅" : "❌"}</td>
                          <td>{emp.sessionsCount}</td>
                          <td>{emp.absencesCount}</td>
                          <td>{formatLastUpdate(emp.lastUpdate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {timesheetMessage && (
            <div className={`message ${timesheetMessage.includes("Erreur") ? "error" : "success"}`}>
              {timesheetMessage}
            </div>
          )}
        </div>
      </div>

      {/* SECTION ARCHIVAGE */}
      <div className="config-section">
        <h2>Archivage des feuilles de temps</h2>
        <div className="config-content">
          <div style={{ 
            background: '#f8f9fa', 
            border: '1px solid #dee2e6', 
            borderRadius: '8px', 
            padding: '20px', 
            marginBottom: '20px' 
          }}>
            <h3 style={{ marginTop: 0, color: '#495057' }}>📁 Gestion des archives</h3>
            <p style={{ color: '#6c757d', marginBottom: '20px' }}>
              Cette fonction permet d'archiver automatiquement les anciennes feuilles de temps. 
              Pour chaque salarié, seuls les <strong>12 mois les plus récents</strong> resteront actifs, 
              les autres seront déplacées vers les archives.
            </p>
            
            <div style={{ 
              background: '#e9ecef', 
              border: '1px solid #ced4da', 
              borderRadius: '6px', 
              padding: '15px', 
              marginBottom: '20px' 
            }}>
              <h4 style={{ marginTop: 0, color: '#495057' }}>⚠️ Attention</h4>
              <ul style={{ margin: '10px 0', paddingLeft: '20px', color: '#6c757d' }}>
                <li>Cette action est <strong>irréversible</strong></li>
                <li>Les feuilles archivées ne seront plus visibles dans les dashboards</li>
                <li>Les archives restent accessibles dans la collection <code>workSessionsWeekArchives</code></li>
                <li>Seuls les 12 mois les plus récents seront conservés</li>
              </ul>
            </div>

            <button 
              className="archive-feuilles-btn" 
              onClick={handleArchiveTimesheets}
              disabled={archiving}
            >
              {archiving ? "🔄 Archivage en cours..." : "🗄️ Archiver les anciennes feuilles"}
            </button>

            {archiveMessage && (
              <div style={{ 
                marginTop: '15px',
                padding: '12px',
                borderRadius: '6px',
                background: archiveMessage.includes("✅") ? '#d4edda' : '#f8d7da',
                color: archiveMessage.includes("✅") ? '#155724' : '#721c24',
                border: `1px solid ${archiveMessage.includes("✅") ? '#c3e6cb' : '#f5c6cb'}`
              }}>
                {archiveMessage}
              </div>
            )}

            {archiveStats && (
              <div style={{ 
                marginTop: '15px',
                background: '#e7f3ff',
                border: '1px solid #b3d9ff',
                borderRadius: '6px',
                padding: '15px'
              }}>
                <h4 style={{ marginTop: 0, color: '#0056b3' }}>📊 Statistiques de l'archivage</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '15px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#0056b3' }}>{archiveStats.totalBefore}</div>
                    <div style={{ fontSize: '12px', color: '#6c757d' }}>Total avant</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>{archiveStats.kept}</div>
                    <div style={{ fontSize: '12px', color: '#6c757d' }}>Conservées</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545' }}>{archiveStats.archived}</div>
                    <div style={{ fontSize: '12px', color: '#6c757d' }}>Archivées</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#0056b3' }}>{archiveStats.totalAfter}</div>
                    <div style={{ fontSize: '12px', color: '#6c757d' }}>Total après</div>
                  </div>
                </div>

                {/* Détails par employé */}
                {archiveStats.employees && archiveStats.employees.length > 0 && (
                  <div style={{ marginTop: '15px' }}>
                    <h5 style={{ marginTop: 0, color: '#495057', fontSize: '14px' }}>📋 Détails par employé</h5>
                    <div style={{ 
                      maxHeight: '200px', 
                      overflowY: 'auto', 
                      background: 'white', 
                      border: '1px solid #dee2e6', 
                      borderRadius: '4px',
                      padding: '10px'
                    }}>
                      {archiveStats.employees.map((emp, index) => (
                        <div key={index} style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          padding: '8px 0',
                          borderBottom: index < archiveStats.employees.length - 1 ? '1px solid #f1f3f4' : 'none'
                        }}>
                          <div style={{ flex: 1, fontSize: '12px', color: '#495057' }}>
                            {emp.email}
                          </div>
                          <div style={{ display: 'flex', gap: '15px', fontSize: '12px' }}>
                            <span style={{ color: '#6c757d' }}>
                              Total: <strong>{emp.total}</strong>
                            </span>
                            <span style={{ color: '#28a745' }}>
                              Gardées: <strong>{emp.kept}</strong>
                            </span>
                            <span style={{ color: '#dc3545' }}>
                              Archivées: <strong>{emp.archived}</strong>
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="archive-section">
        <h3>🗄️ Archivage des sessions de travail</h3>
        <div className="archive-alert">
          <strong>⚠️ Attention</strong><br />
          Cette action est <b>irréversible</b>.<br />
          <ul style={{ margin: '8px 0 0 18px', padding: 0 }}>
            <li>Les sessions archivées ne seront plus visibles dans le dashboard principal</li>
            <li>Les pauses associées sont également archivées</li>
            <li>Seules les sessions des 100 derniers jours resteront actives</li>
            <li><b>Les sessions archivées sont disponibles depuis la page Archives (onglet Sessions).</b></li>
          </ul>
        </div>
        <button
          className="archive-feuilles-btn"
          onClick={() => {
            if (window.confirm('⚠️ Cette action va déplacer toutes les sessions de plus de 100 jours dans les archives. Voulez-vous continuer ?')) {
              handleArchiveSessions();
            }
          }}
          disabled={archivingSessions}
        >
          <span role="img" aria-label="archive">🗄️</span>
          {archivingSessions ? "Archivage en cours..." : "Archiver les sessions anciennes"}
        </button>
        {archiveSessionsMsg && (
          <div style={{ marginTop: 8, color: archiveSessionsMsg.startsWith('Erreur') ? 'red' : 'green' }}>
            {archiveSessionsMsg}
          </div>
        )}
      </div>

      <div className="config-section">
        <h3>Alerte Temps de Session Max</h3>
        <label>
          Temps maximal d'une session (heures) :
          <input
            type="number"
            min="1"
            max="48"
            step="0.5"
            value={maxSessionTime}
            onChange={e => setMaxSessionTime(e.target.value)}
            style={{ marginLeft: 8, width: 60 }}
          />
        </label>
        <button onClick={saveMaxSessionTime} style={{ marginLeft: 16 }}>
          Enregistrer
        </button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
        <button className="logout-history-button small-logout" onClick={handleLogout}>
          🔒 Déconnexion
        </button>
      </div>
    </div>
  );
}

export default ConfigurationPage;